import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { CURRENCIES, CURRENCY } from '../utils/constants'
import { useSettings } from './SettingsContext'

const CurrencyContext = createContext()

// Global reference currency for baseline data normalization
const BASELINE_CURRENCY = 'USD'

export const useCurrency = () => {
  const context = useContext(CurrencyContext)
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider')
  }
  return context
}

export const CurrencyProvider = ({ children }) => {
  const { settings, fetchSettings } = useSettings()
  
  // Active tenant currency from System Settings
  const baseCurrency = useMemo(() => {
    return settings.currency && CURRENCIES[settings.currency] 
      ? settings.currency 
      : CURRENCY.DEFAULT
  }, [settings.currency])

  // Normalized exchange rate dictionary relative to baseline
  const rates = useMemo(() => {
    const rawRates = settings.currency_rates || {}
    const defaultRates = {
      USD: 1,
      EUR: 0.92,
      GBP: 0.79,
      INR: 83.50,
      AED: 3.67,
      SAR: 3.75,
      SGD: 1.34,
      CAD: 1.36,
      AUD: 1.52,
      JPY: 154.50,
      CHF: 0.90,
      CNY: 7.24,
      BRL: 5.65,
    }

    const merged = { ...defaultRates }
    if (rawRates && typeof rawRates === 'object') {
      Object.keys(rawRates).forEach((k) => {
        const val = parseFloat(rawRates[k])
        if (!isNaN(val) && val > 0) {
          merged[k] = val
        }
      })
    }
    return merged
  }, [settings.currency_rates])

  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    return (settings.currency && CURRENCIES[settings.currency]) ? settings.currency : baseCurrency
  })

  // Synchronize selected currency whenever Tenant Admin modifies system currency or tenant scope loads
  useEffect(() => {
    if (settings.currency && CURRENCIES[settings.currency]) {
      setSelectedCurrency(settings.currency)
      localStorage.setItem('selectedCurrency', settings.currency)
    }
  }, [settings.currency])

  // Cross-tab broadcast synchronization
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedCurrency' && e.newValue && CURRENCIES[e.newValue]) {
        setSelectedCurrency(e.newValue)
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const getCurrencyInfo = useCallback((currencyCode = selectedCurrency) => {
    return CURRENCIES[currencyCode] || CURRENCIES[baseCurrency] || CURRENCIES[CURRENCY.DEFAULT]
  }, [selectedCurrency, baseCurrency])

  /**
   * Convert an amount from source currency to target currency using the universal cross-rate formula:
   * converted = amount * (Rate(target) / Rate(source))
   */
  const convertAmount = useCallback((amount, fromCurrency = BASELINE_CURRENCY, toCurrency = selectedCurrency) => {
    if (amount === null || amount === undefined || isNaN(amount)) return 0
    const num = parseFloat(amount)
    if (!fromCurrency || !toCurrency || fromCurrency === toCurrency) return num

    const fromRate = parseFloat(rates[fromCurrency]) || 1
    const toRate = parseFloat(rates[toCurrency]) || 1

    if (!isNaN(fromRate) && fromRate > 0 && !isNaN(toRate) && toRate > 0) {
      return (num / fromRate) * toRate
    }

    return num
  }, [rates, selectedCurrency])

  /**
   * Format currency values into localized string representations.
   * Supports both (amount, options) and (amount, currencyCode, options) signatures.
   * Defaults fromCurrency to BASELINE_CURRENCY so any figure displayed in the app
   * converts automatically when the user changes selectedCurrency.
   */
  const formatCurrency = useCallback((amount, currencyOrOptions, maybeOptions) => {
    let targetCurrency = selectedCurrency
    let options = {}

    if (typeof currencyOrOptions === 'string' && currencyOrOptions.trim()) {
      targetCurrency = currencyOrOptions
      if (typeof maybeOptions === 'object' && maybeOptions !== null) {
        options = maybeOptions
      }
    } else if (typeof currencyOrOptions === 'object' && currencyOrOptions !== null) {
      options = currencyOrOptions
      if (typeof options.toCurrency === 'string' && options.toCurrency.trim()) {
        targetCurrency = options.toCurrency
      } else if (typeof options.currency === 'string' && options.currency.trim()) {
        targetCurrency = options.currency
      }
    }

    if (amount === null || amount === undefined || isNaN(amount)) {
      const info = getCurrencyInfo(targetCurrency)
      return info.position === 'after' ? `0.00 ${info.symbol}` : `${info.symbol}0.00`
    }

    const { fromCurrency = BASELINE_CURRENCY, skipConversion = false, ...intlOptions } = options
    const currency = getCurrencyInfo(targetCurrency)
    
    let numericAmount = parseFloat(amount)
    if (!skipConversion && fromCurrency && fromCurrency !== targetCurrency) {
      numericAmount = convertAmount(numericAmount, fromCurrency, targetCurrency)
    }

    const defaultOptions = {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...intlOptions
    }

    try {
      return new Intl.NumberFormat(currency.locale || 'en-US', defaultOptions).format(numericAmount)
    } catch (error) {
      const formattedNumber = numericAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return currency.position === 'after' 
        ? `${formattedNumber} ${currency.symbol}`
        : `${currency.symbol}${formattedNumber}`
    }
  }, [selectedCurrency, getCurrencyInfo, convertAmount])

  /**
   * Compact notation formatting (e.g. $1.2M, ₹4.5L, £850K)
   * Defaults fromCurrency to baseCurrency so compact KPI figures convert automatically.
   */
  const formatCurrencyCompact = useCallback((amount, currencyOrOptions, maybeOptions) => {
    let targetCurrency = selectedCurrency
    let options = {}

    if (typeof currencyOrOptions === 'string' && currencyOrOptions.trim()) {
      targetCurrency = currencyOrOptions
      if (typeof maybeOptions === 'object' && maybeOptions !== null) {
        options = maybeOptions
      }
    } else if (typeof currencyOrOptions === 'object' && currencyOrOptions !== null) {
      options = currencyOrOptions
      if (typeof options.toCurrency === 'string' && options.toCurrency.trim()) {
        targetCurrency = options.toCurrency
      } else if (typeof options.currency === 'string' && options.currency.trim()) {
        targetCurrency = options.currency
      }
    }

    if (amount === null || amount === undefined || isNaN(amount)) {
      const info = getCurrencyInfo(targetCurrency)
      return info.position === 'after' ? `0 ${info.symbol}` : `${info.symbol}0`
    }

    const { fromCurrency = BASELINE_CURRENCY, skipConversion = false, ...intlOptions } = options
    const currency = getCurrencyInfo(targetCurrency)
    
    let numericAmount = parseFloat(amount)
    if (!skipConversion && fromCurrency && fromCurrency !== targetCurrency) {
      numericAmount = convertAmount(numericAmount, fromCurrency, targetCurrency)
    }

    // Indian Lakh / Crore notation when INR is active
    if (targetCurrency === 'INR') {
      const absVal = Math.abs(numericAmount)
      const sign = numericAmount < 0 ? '-' : ''
      if (absVal >= 10000000) {
        return `${sign}${currency.symbol}${(absVal / 10000000).toFixed(2)} Cr`
      } else if (absVal >= 100000) {
        return `${sign}${currency.symbol}${(absVal / 100000).toFixed(2)} L`
      } else if (absVal >= 1000) {
        return `${sign}${currency.symbol}${(absVal / 1000).toFixed(1)} K`
      }
      return `${sign}${currency.symbol}${absVal.toFixed(0)}`
    }

    try {
      return new Intl.NumberFormat(currency.locale || 'en-US', {
        style: 'currency',
        currency: currency.code,
        notation: 'compact',
        maximumFractionDigits: 1,
        ...intlOptions
      }).format(numericAmount)
    } catch (error) {
      let value = numericAmount
      let suffix = ''
      if (Math.abs(value) >= 1000000) {
        value = value / 1000000
        suffix = 'M'
      } else if (Math.abs(value) >= 1000) {
        value = value / 1000
        suffix = 'K'
      }
      const formatted = value.toFixed(1)
      return currency.position === 'after' 
        ? `${formatted}${suffix} ${currency.symbol}`
        : `${currency.symbol}${formatted}${suffix}`
    }
  }, [selectedCurrency, getCurrencyInfo, convertAmount])

  const getCurrencySymbol = useCallback((currencyCode = selectedCurrency) => {
    const info = getCurrencyInfo(currencyCode)
    return info?.symbol || '$'
  }, [getCurrencyInfo, selectedCurrency])

  /**
   * Directly convert and format from source document currency to target display currency
   */
  const formatWithConversion = useCallback((amount, fromCurrency = baseCurrency, toCurrency = selectedCurrency, options = {}) => {
    return formatCurrency(amount, toCurrency, { fromCurrency, ...options })
  }, [formatCurrency, baseCurrency, selectedCurrency])

  const parseCurrencyInput = useCallback((input) => {
    if (!input) return 0
    const cleaned = input.toString()
      .replace(/[^\d.-]/g, '')
      .replace(/^-+/, '-')
      .replace(/-(?!^)/g, '')
    const parsed = parseFloat(cleaned)
    return isNaN(parsed) ? 0 : parsed
  }, [])

  const changeCurrency = useCallback((currencyCode) => {
    if (CURRENCIES[currencyCode]) {
      setSelectedCurrency(currencyCode)
      localStorage.setItem('selectedCurrency', currencyCode)
    }
  }, [])

  const refreshCurrencyConfig = useCallback(async () => {
    if (fetchSettings) {
      await fetchSettings()
    }
    return Promise.resolve()
  }, [fetchSettings])

  const value = {
    selectedCurrency,
    baseCurrency,
    rates,
    currencyInfo: getCurrencyInfo(),
    currencies: CURRENCIES,
    formatCurrency,
    formatCurrencyCompact,
    formatWithConversion,
    getCurrencySymbol,
    convertAmount,
    parseCurrencyInput,
    changeCurrency,
    getCurrencyInfo,
    refreshCurrencyConfig
  }

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  )
}
